import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { updateOrder, getOrderById, getMedicines, getPharmacies, getStates } from '../services/api';
import { formatCurrency } from '../utils/formatters';

// Styled components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: var(--card-bg);
  border-radius: 8px;
  padding: 1.5rem;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 1rem;
  margin-bottom: 1rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-secondary);
  
  &:hover {
    color: var(--text-primary);
  }
`;

const ModalBody = styled.div`
  flex-grow: 1;
  overflow-y: auto;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  border-top: 1px solid var(--border-color);
  padding-top: 1rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 0.875rem;
  background-color: var(--input-bg);
  color: var(--text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
  }
`;

const TextArea = styled.textarea`
  padding: 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 0.875rem;
  min-height: 100px;
  resize: vertical;
  background-color: var(--input-bg);
  color: var(--text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
  }
`;

const Select = styled.select`
  padding: 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 0.875rem;
  background-color: var(--input-bg);
  color: var(--text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
  }
`;

const SectionTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 1.5rem 0 1rem;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 0.5rem;
`;

const MedicineRow = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr 2fr 1fr 1fr;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  align-items: start;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding-bottom: 0.5rem;
    border-bottom: 1px dashed var(--border-color);
  }
`;

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: var(--error-bg);
  color: var(--error);
  border: none;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  
  &:hover {
    background-color: var(--error-bg-hover);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: var(--hover-color);
  color: var(--text-primary);
  border: 1px dashed var(--border-color);
  border-radius: 4px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.875rem;
  width: 100%;
  margin-top: 0.5rem;
  
  &:hover {
    background-color: var(--hover-color-darker);
  }
  
  svg {
    width: 16px;
    height: 16px;
    margin-right: 0.5rem;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error);
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const OrderSummary = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: var(--hover-color);
  border-radius: 4px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);
  
  ${props => props.total && `
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--border-color);
    font-weight: 600;
    font-size: 1rem;
  `}
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: var(--bg-secondary-button);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  
  &:hover:not(:disabled) {
    background-color: var(--bg-secondary-button-hover);
  }
`;

const SaveButton = styled(Button)`
  background-color: var(--primary-color);
  color: white;
  border: none;
  
  &:hover:not(:disabled) {
    background-color: var(--primary-dark);
  }
`;

function EditOrderModal({ isOpen, onClose, orderData, onOrderUpdated }) {
  const [order, setOrder] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Reference data
  const [medicines, setMedicines] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [states, setStates] = useState([]);
  
  // Initialize form state with orderData
  useEffect(() => {
    if (orderData) {
      setOrder({
        ...orderData,
        // Ensure order_items is an array with price field
        order_items: Array.isArray(orderData.order_items) 
          ? orderData.order_items.map(item => ({
              ...item,
              price: item.price || (item.variant ? item.variant.price : 0) || 0
            })) 
          : []
      });
      
      // Fetch reference data
      const fetchReferenceData = async () => {
        setLoading(true);
        try {
          const [medicinesData, pharmaciesData, statesData] = await Promise.all([
            getMedicines(),
            getPharmacies(),
            getStates()
          ]);
          
          setMedicines(medicinesData);
          setPharmacies(pharmaciesData);
          setStates(statesData);
        } catch (err) {
          console.error('Error fetching reference data:', err);
          setError('Error loading form data. Please try again later.');
        } finally {
          setLoading(false);
        }
      };
      
      fetchReferenceData();
    }
  }, [orderData]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrder(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleMedicineChange = (index, field, value) => {
    setOrder(prev => {
      const updatedItems = [...prev.order_items];
      
      // If changing medicine, we need to reset quantity and dosage
      if (field === 'medicine_id') {
        const selectedMedicine = medicines.find(med => med.medicine_id === parseInt(value));
        
        updatedItems[index] = {
          ...updatedItems[index],
          medicine_id: parseInt(value),
          medicine: selectedMedicine,
          variant_id: null,
          variant: null,
          dosage_instruction_id: null,
          dosage_instruction: null,
          price: 0
        };
      } else if (field === 'variant_id') {
        const medicine = updatedItems[index].medicine;
        const selectedVariant = medicine?.variants?.find(v => v.variant_id === parseInt(value));
        
        updatedItems[index] = {
          ...updatedItems[index],
          variant_id: parseInt(value),
          variant: selectedVariant,
          price: selectedVariant?.price || 0
        };
      } else if (field === 'dosage_instruction_id') {
        const medicine = updatedItems[index].medicine;
        const selectedDosage = medicine?.dosage_instructions?.find(d => d.dosage_id === parseInt(value));
        
        updatedItems[index] = {
          ...updatedItems[index],
          dosage_instruction_id: parseInt(value),
          dosage_instruction: selectedDosage
        };
      } else if (field === 'price') {
        // Handle direct price changes
        updatedItems[index] = {
          ...updatedItems[index],
          [field]: parseFloat(value) || 0
        };
      } else {
        updatedItems[index] = {
          ...updatedItems[index],
          [field]: value
        };
      }
      
      return { ...prev, order_items: updatedItems };
    });
  };
  
  const handleAddMedicine = () => {
    setOrder(prev => ({
      ...prev,
      order_items: [
        ...prev.order_items,
        {
          medicine_id: null,
          medicine: null,
          variant_id: null,
          variant: null,
          dosage_instruction_id: null,
          dosage_instruction: null,
          price: 0
        }
      ]
    }));
  };
  
  const handleRemoveMedicine = (index) => {
    setOrder(prev => {
      const updatedItems = [...prev.order_items];
      updatedItems.splice(index, 1);
      return {
        ...prev,
        order_items: updatedItems
      };
    });
  };
  
  const calculateTotals = () => {
    if (!order.order_items) return { subtotal: 0, total: 0 };
    
    const subtotal = order.order_items.reduce((sum, item) => {
      const price = parseFloat(item.price) || 0;
      return sum + price;
    }, 0);
    
    const shippingFee = parseFloat(order.shipping_fee) || 0;
    const discount = parseFloat(order.discount) || 0;
    const total = subtotal + shippingFee - discount;
    
    return {
      subtotal,
      shippingFee,
      discount,
      total
    };
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    const totals = calculateTotals();
    
    // Prepare the data
    const updatedOrder = {
      ...order,
      shipping_fee: parseFloat(order.shipping_fee) || 0,
      discount: parseFloat(order.discount) || 0,
      total_fee: totals.total,
      pharmacy_id: parseInt(order.pharmacy?.pharmacy_id) || parseInt(order.pharmacy_id) || null,
      // Make sure we have a valid patient_id
      patient_id: parseInt(order.patient?.patient_id) || parseInt(order.patient_id) || null,
      shipping_address_id: parseInt(order.shipping_address?.address_id) || parseInt(order.shipping_address_id) || null,
      use_new_address: false, // Always false for existing orders 
      // Map order items to the format expected by the API
      order_items: order.order_items.map(item => ({
        medicine_id: parseInt(item.medicine_id) || parseInt(item.medicine?.medicine_id),
        variant_id: parseInt(item.variant_id) || parseInt(item.variant?.variant_id),
        dosage_instruction_id: parseInt(item.dosage_instruction_id) || parseInt(item.dosage_instruction?.dosage_id),
        price: parseFloat(item.price) || 0, // Make sure price is included
        compounding_context: (item.compounding_context || "").trim()
      }))
    };
    
    try {
      // Update the order
      await updateOrder(order.order_id, updatedOrder);
      
      // Notify parent component
      if (onOrderUpdated) {
        onOrderUpdated();
      }
      
      // Close the modal
      onClose();
    } catch (err) {
      console.error('Error updating order:', err);
      setError(`Failed to update order: ${err.message || 'An unexpected error occurred'}`);
    } finally {
      setSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  const totals = calculateTotals();
  
  return (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Edit Order Request #{orderData.order_id}</ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        
        {loading ? (
          <ModalBody>Loading order data...</ModalBody>
        ) : (
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <FormGrid>
                <FormGroup>
                  <Label>Pharmacy</Label>
                  <Select 
                    name="pharmacy_id"
                    value={order.pharmacy?.pharmacy_id || ''}
                    onChange={e => {
                      const selectedPharmacy = pharmacies.find(p => p.pharmacy_id === parseInt(e.target.value));
                      setOrder(prev => ({
                        ...prev,
                        pharmacy_id: parseInt(e.target.value),
                        pharmacy: selectedPharmacy
                      }));
                    }}
                    required
                  >
                    <option value="">Select Pharmacy</option>
                    {pharmacies.map(pharmacy => (
                      <option key={pharmacy.pharmacy_id} value={pharmacy.pharmacy_id}>
                        {pharmacy.pharmacy_name}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
                
                <FormGroup>
                  <Label>Order Type</Label>
                  <Select
                    name="order_type"
                    value={order.order_type || 'new'}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="new">New</option>
                    <option value="refill">Refill</option>
                  </Select>
                </FormGroup>
              </FormGrid>
              
              <FormGroup>
                <Label>Notes</Label>
                <TextArea
                  name="notes"
                  value={order.notes || ''}
                  onChange={handleInputChange}
                  placeholder="Add any special instructions or notes about this order"
                />
              </FormGroup>
              
              <SectionTitle>Medicines</SectionTitle>
              
              {order.order_items && order.order_items.length > 0 ? (
                <>
                  <MedicineRow>
                    <Label>Medicine</Label>
                    <Label>Variant</Label>
                    <Label>Dosage Instructions</Label>
                    <Label>Price ($)</Label>
                    <div></div> {/* Spacer for remove button */}
                  </MedicineRow>
                  
                  {order.order_items.map((item, index) => (
                    <MedicineRow key={index}>
                      <Select
                        value={item.medicine?.medicine_id || ''}
                        onChange={e => handleMedicineChange(index, 'medicine_id', e.target.value)}
                        required
                      >
                        <option value="">Select Medicine</option>
                        {medicines.map(med => (
                          <option key={med.medicine_id} value={med.medicine_id}>
                            {med.medicine_name}
                          </option>
                        ))}
                      </Select>
                      
                      <Select
                        value={item.variant?.variant_id || ''}
                        onChange={e => handleMedicineChange(index, 'variant_id', e.target.value)}
                        disabled={!item.medicine}
                        required
                      >
                        <option value="">Select Variant</option>
                        {item.medicine?.variants?.map(variant => (
                          <option key={variant.variant_id} value={variant.variant_id}>
                            {variant.quantity} {variant.unit} - {formatCurrency(variant.price)}
                          </option>
                        ))}
                      </Select>
                      
                      <Select
                        value={item.dosage_instruction?.dosage_id || ''}
                        onChange={e => handleMedicineChange(index, 'dosage_instruction_id', e.target.value)}
                        disabled={!item.medicine}
                        required
                      >
                        <option value="">Select Instructions</option>
                        {item.medicine?.dosage_instructions?.map(instr => (
                          <option key={instr.dosage_id} value={instr.dosage_id}>
                            {instr.dosage_instruction}
                          </option>
                        ))}
                      </Select>
                      
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.price || 0}
                        onChange={e => handleMedicineChange(index, 'price', e.target.value)}
                        placeholder="0.00"
                      />
                      
                      <RemoveButton 
                        type="button"
                        onClick={() => handleRemoveMedicine(index)}
                        title="Remove medicine"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </RemoveButton>
                    </MedicineRow>
                  ))}
                </>
              ) : (
                <div>No medicines added yet.</div>
              )}
              
              <AddButton
                type="button"
                onClick={handleAddMedicine}
                disabled={order.order_items?.length >= 5}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 3.33334V12.6667" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M3.33301 8H12.6663" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Add Medicine
              </AddButton>
              
              {order.order_items?.length >= 5 && (
                <ErrorMessage>Maximum 5 medicines per order.</ErrorMessage>
              )}
              
              <SectionTitle>Pricing</SectionTitle>
              
              <FormGrid>
                <FormGroup>
                  <Label>Shipping Fee ($)</Label>
                  <Input
                    type="number"
                    name="shipping_fee"
                    min="0"
                    step="0.01"
                    value={order.shipping_fee || 0}
                    onChange={handleInputChange}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Discount ($)</Label>
                  <Input
                    type="number"
                    name="discount"
                    min="0"
                    step="0.01"
                    value={order.discount || 0}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </FormGrid>
              
              <OrderSummary>
                <SummaryRow>
                  <span>Subtotal:</span>
                  <span>{formatCurrency(totals.subtotal)}</span>
                </SummaryRow>
                <SummaryRow>
                  <span>Shipping Fee:</span>
                  <span>{formatCurrency(parseFloat(order.shipping_fee) || 0)}</span>
                </SummaryRow>
                <SummaryRow>
                  <span>Discount:</span>
                  <span>{formatCurrency(parseFloat(order.discount) || 0)}</span>
                </SummaryRow>
                <SummaryRow total>
                  <span>Total:</span>
                  <span>{formatCurrency(totals.total)}</span>
                </SummaryRow>
              </OrderSummary>
              
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </ModalBody>
            
            <ModalFooter>
              <CancelButton type="button" onClick={onClose} disabled={submitting}>
                Cancel
              </CancelButton>
              <SaveButton type="submit" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save Changes'}
              </SaveButton>
            </ModalFooter>
          </form>
        )}
      </ModalContent>
    </ModalOverlay>
  );
}

export default EditOrderModal;